import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Megaphone, Plus, Trash2, Edit, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  DataTable,
  Pagination,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  SearchInput,
  FilterCombobox,
  useAuth,
} from '@aaas/shared';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { useCampaignListQuery } from '../../hooks/useCampaignQueries';
import campaignService from '../../services/campaign.service';
import { CampaignFormDialog } from '../../components/CampaignFormDialog';
import { useATSRole } from '../../hooks/useATSRole';
import type { Campaign } from '../../types';

export function CampaignListPage() {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { t } = useTranslation('ats');
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({ filterKeys: ['search', 'status'] });
  const searchValue = filters.search as string | undefined;
  const statusValue = filters.status as string | undefined;

  const statusOptions = [
    { value: 'active', label: t('campaign.statusActive') },
    { value: 'inactive', label: t('campaign.statusInactive') },
  ];
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  // React Query for Campaign list
  const {
    data: campaignResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useCampaignListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    search: searchValue,
    status: statusValue,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  // Extract data from response
  const campaigns = campaignResponse?.data?.items || [];
  const totalItems = campaignResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingCampaign(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((record: Campaign) => {
    setFormMode('edit');
    setEditingCampaign(record);
    setShowFormDialog(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowFormDialog(false);
    setEditingCampaign(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteClick = useCallback((record: Campaign) => {
    setSelectedCampaign(record);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedCampaign || !user || !currentWorkspace) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedCampaign.id));

    try {
      const token = await user.getIdToken();
      await campaignService.deleteCampaign(selectedCampaign.id, currentWorkspace.id, token);
      await refetch();
      setShowDeleteDialog(false);
      setSelectedCampaign(null);
    } catch (err: unknown) {
      let errorMessage = t('config.unknownError');
      if (axios.isAxiosError(err)) {
        errorMessage =
          err.response?.data?.message || err.response?.data?.detail || err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedCampaign) {
          newSet.delete(selectedCampaign.id);
        }
        return newSet;
      });
    }
  };

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedCampaign(null);
      setDeleteError(null);
    }
  };

  // Define table columns
  const columns: DataTableColumn<Campaign>[] = [
    {
      key: 'code',
      title: t('campaign.columnCode'),
      width: 120,
      sortable: true,
      render: (value: string) => (
        <span className="font-mono text-sm font-medium text-purple-600">{value}</span>
      ),
    },
    {
      key: 'name',
      title: t('campaign.columnName'),
      sortable: true,
      render: (value: string, record: Campaign) => (
        <button
          onClick={() => navigate(`/ats/campaigns/${record.id}`)}
          className="font-medium text-purple-600 hover:text-purple-700 hover:underline text-left"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'description',
      title: t('campaign.columnDescription'),
      render: (value: string | null) => (
        <span className="text-gray-600 truncate max-w-xs block">{value || '-'}</span>
      ),
    },
    {
      key: 'status',
      title: t('campaign.columnStatus'),
      width: 100,
      sortable: true,
      render: (value: string) => (
        <Badge
          className={
            value === 'active'
              ? 'bg-green-100 text-green-800 hover:bg-green-100'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
          }
        >
          {value === 'active' ? t('campaign.statusActive') : t('campaign.statusInactive')}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      title: t('config.columnCreated'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  // Define table actions (only for leader+)
  const actions: DataTableAction<Campaign>[] = isLeaderOrAbove
    ? [
        {
          key: 'edit',
          label: t('config.edit'),
          icon: <Edit className="h-4 w-4" />,
          onClick: handleEditClick,
          inline: true,
        },
        {
          key: 'delete',
          label: t('config.delete'),
          icon: <Trash2 className="h-4 w-4" />,
          loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
          onClick: handleDeleteClick,
          loading: (record) => deletingIds.has(record.id),
          destructive: true,
          inline: true,
        },
      ]
    : [];

  if (isLoading && !campaignResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="h-8 w-8 text-purple-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('campaign.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">{t('campaign.pageDescription')}</p>
          </div>
        </div>
        {isLeaderOrAbove && (
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('campaign.newOne')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <SearchInput
          placeholder={t('campaign.searchPlaceholder')}
          onSearch={(value) => setFilters({ ...filters, search: value || undefined })}
          initialValue={searchValue || ''}
        />
        <FilterCombobox
          options={statusOptions}
          value={statusValue}
          onChange={(value) => setFilters({ ...filters, status: value })}
          placeholder={t('campaign.columnStatus')}
          searchPlaceholder={t('campaign.statusSearchPlaceholder')}
          emptyMessage={t('campaign.statusEmpty')}
          className="w-[140px]"
        />
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('campaign.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={campaigns}
          loading={isLoading && !campaignResponse}
          actions={actions}
          rowKey={(record) => record.id}
          emptyMessage={t('campaign.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('campaign.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('campaign.deleteBody')}</AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Campaign Dialog */}
      <CampaignFormDialog
        open={showFormDialog}
        onClose={handleFormClose}
        mode={formMode}
        campaign={editingCampaign}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
